import { useState, useEffect, lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { store, RootState } from './store/store'
import { setConversations } from './store/slices/conversationSlice'
import { logout, updateUser } from './store/slices/authSlice'
import ErrorBoundary from './components/ErrorBoundary'
import LandingPage from './pages/LandingPage'
import CookieConsent from './components/CookieConsent'
import { NotificationProvider } from './contexts/NotificationContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { AnalysisNotificationProvider } from './contexts/AnalysisNotificationContext'
import { apiService, authService } from './services/api'
import { Namespace } from './types'
import { alert, toast } from './lib/alerts'
import { trackEvent, trackPageView } from './lib/analytics'
import { Menu, PanelLeftOpen, LogOut, Mail, X } from 'lucide-react'
import './index.css'

// Lazy-load everything outside the landing page so the initial bundle stays small.
const ChatInterface = lazy(() => import('./components/chat/ChatInterface'))
const Sidebar = lazy(() => import('./components/sidebar/Sidebar'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const AuditView = lazy(() => import('./pages/AuditView'))
const AuditsPage = lazy(() => import('./pages/AuditsPage'))
const UploadPage = lazy(() => import('./pages/UploadPage'))
const RegulationsPage = lazy(() => import('./pages/RegulationsPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'))
const SharedAuditPage = lazy(() => import('./pages/SharedAuditPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'))
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'))
const SecurityPage = lazy(() => import('./pages/SecurityPage'))

const RouteFallback = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-dark-bg">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" aria-label="Loading" />
  </div>
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
})

type PageType = 'landing' | 'home' | 'upload' | 'chat' | 'dashboard' | 'audits' | 'audit' | 'regulations' | 'settings' | 'billing' | 'feedback' | 'terms' | 'privacy' | 'refund-policy' | 'cookie-policy' | 'security' | 'login' | 'register' | 'forgot-password' | 'reset-password' | 'verify-email' | 'contact' | 'shared-audit'

const PAGE_PATHS: Record<string, PageType> = {
  '/': 'landing',
  '/dashboard': 'dashboard',
  '/upload': 'upload',
  '/chat': 'chat',
  '/audits': 'audits',
  '/audit': 'audit',
  '/regulations': 'regulations',
  '/billing': 'billing',
  '/settings': 'settings',
  '/feedback': 'feedback',
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/refund-policy': 'refund-policy',
  '/cookie-policy': 'cookie-policy',
  '/security': 'security',
  '/contact': 'contact',
  '/login': 'login',
  '/register': 'register',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/verify-email': 'verify-email',
}

const PATH_FOR_PAGE: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_PATHS).map(([path, page]) => [page, path])
)

// Helper to get persisted page state
const getPersistedPage = (): PageType => {
  // URL path takes priority over sessionStorage
  const pathPage = PAGE_PATHS[window.location.pathname]
  if (pathPage) return pathPage

  try {
    const saved = sessionStorage.getItem('currentPage')
    if (saved && ['landing', 'home', 'upload', 'chat', 'dashboard', 'audits', 'audit', 'regulations', 'settings', 'billing', 'feedback', 'terms', 'privacy', 'refund-policy', 'cookie-policy', 'security', 'login', 'register', 'forgot-password', 'reset-password', 'verify-email', 'contact', 'shared-audit'].includes(saved)) {
      return saved as PageType
    }
  } catch {}
  return 'landing'
}

function AppContent() {
  const dispatch = useDispatch()
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [currentPage, setCurrentPageState] = useState<PageType>(getPersistedPage)
  const [namespaces, setNamespaces] = useState<Namespace[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [selectedAuditId, setSelectedAuditId] = useState<string | undefined>(() => {
    try { return sessionStorage.getItem('selectedAuditId') || undefined } catch { return undefined }
  })
  const [showSidebar, setShowSidebar] = useState(() => {
    try { return sessionStorage.getItem('showSidebar') === 'true' } catch { return false }
  })
  const [resetToken, setResetToken] = useState<string>('')
  const [verifyToken, setVerifyToken] = useState<string>('')
  const [sharedAuditToken, setSharedAuditToken] = useState<string>('')
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [resendingVerification, setResendingVerification] = useState(false)

  // Wrapper to persist page changes and sync URL
  const setCurrentPage = (page: PageType) => {
    setCurrentPageState(page)
    try { sessionStorage.setItem('currentPage', page) } catch {}

    const targetPath = PATH_FOR_PAGE[page] || '/'
    if (window.location.pathname !== targetPath) {
      window.history.pushState({ page }, '', targetPath)
    }
  }

  // Persist sidebar and audit state
  useEffect(() => {
    try { sessionStorage.setItem('showSidebar', showSidebar.toString()) } catch {}
  }, [showSidebar])

  useEffect(() => {
    try {
      if (selectedAuditId) sessionStorage.setItem('selectedAuditId', selectedAuditId)
      else sessionStorage.removeItem('selectedAuditId')
    } catch {}
  }, [selectedAuditId])

  // Update canonical link to match current page URL
  useEffect(() => {
    const link = document.getElementById('canonical-link') as HTMLLinkElement | null
    if (link) {
      const path = PATH_FOR_PAGE[currentPage] || '/'
      link.href = `https://auditguard.org${path}`
    }
    trackPageView(currentPage)
  }, [currentPage])

  // Set initial history state and handle browser back/forward
  useEffect(() => {
    window.history.replaceState({ page: currentPage }, '')

    const handlePopState = (e: PopStateEvent) => {
      const page = (e.state?.page as PageType) || PAGE_PATHS[window.location.pathname] || 'landing'
      setCurrentPageState(page)
      try { sessionStorage.setItem('currentPage', page) } catch {}
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Redirect to dashboard if authenticated and on landing/login
  useEffect(() => {
    if (isAuthenticated && (currentPage === 'landing' || currentPage === 'login' || currentPage === 'register')) {
      setCurrentPage('dashboard')
      setShowSidebar(true)
    } else if (!isAuthenticated && !['landing', 'login', 'register', 'forgot-password', 'reset-password', 'verify-email', 'contact', 'terms', 'privacy', 'refund-policy', 'cookie-policy', 'security', 'shared-audit'].includes(currentPage)) {
      setCurrentPage('landing')
      setShowSidebar(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const checkoutResult = urlParams.get('checkout')
    const sharedMatch = window.location.pathname.match(/^\/shared\/(.+)$/)
    if (sharedMatch) {
      setSharedAuditToken(sharedMatch[1])
      setCurrentPage('shared-audit')
      return
    }

    if (token && window.location.pathname.includes('reset-password')) {
      setResetToken(token)
      setCurrentPage('reset-password')
    } else if (token && window.location.pathname.includes('verify-email')) {
      setVerifyToken(token)
      setCurrentPage('verify-email')
    } else if (checkoutResult) {
      // Clean URL params after reading
      window.history.replaceState({}, '', window.location.pathname)
      if (checkoutResult === 'success') {
        toast.success('Payment successful! Your credits have been added.', 'Checkout Complete')
        setCurrentPage('billing')
        setShowSidebar(true)
      } else if (checkoutResult === 'cancel') {
        toast.info('Checkout cancelled.', 'Checkout')
        setCurrentPage('billing')
        setShowSidebar(true)
      }
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return

    const loadInitialData = async () => {
      try {
        const response = await apiService.getNamespaces()

        if (response.data.namespaces_with_stats) {
          const namespaceList = response.data.namespaces_with_stats.map((ns: { name: string; vector_count?: number }, index: number) => ({
            id: `namespace_${index}`,
            name: ns.name,
            document_count: ns.vector_count || 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }))
          setNamespaces(namespaceList)
        } else if (response.data.namespaces) {
          const nsList = response.data.namespaces
          // Handle both object array and string array formats
          const namespaceList = nsList.map((ns: string | { namespace?: string; name?: string; vector_count?: number }, index: number) => {
            if (typeof ns === 'object') {
              return {
                id: `namespace_${index}`,
                name: ns.namespace || ns.name || '',
                document_count: ns.vector_count || 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            }
            return {
              id: `namespace_${index}`,
              name: ns,
              document_count: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          })
          setNamespaces(namespaceList)
        }
      } catch {
        // Silent fail for initial data load
      }

      try {
        const response = await apiService.getConversations()
        // Handle both paginated (Django) and non-paginated responses
        dispatch(setConversations(response.data.results || response.data.conversations || []))
      } catch (error) {
        // Silently handle conversation load failure
      }
    }
    loadInitialData()
  }, [dispatch, isAuthenticated])

  const handleUpload = async (files: File[], namespace: string, category?: string, tags?: string) => {
    setIsUploading(true)
    trackEvent('upload_started', {
      file_count: files.length,
      category: category || 'general',
      has_tags: Boolean(tags),
    })
    try {
      const existingNamespaces = namespaces.map(ns => ns.name)
      const isNewNamespace = !existingNamespaces.includes(namespace)

      for (const file of files) {
        await apiService.uploadDocument(file, namespace, isNewNamespace, category, tags)
      }

      const namespacesResponse = await apiService.getNamespaces()

      if (namespacesResponse.data.namespaces_with_stats) {
        const namespaceList = namespacesResponse.data.namespaces_with_stats.map((ns: { name: string; vector_count?: number }, index: number) => ({
          id: `namespace_${index}`,
          name: ns.name,
          document_count: ns.vector_count || 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }))
        setNamespaces(namespaceList)
      } else if (namespacesResponse.data.namespaces) {
        const nsList = namespacesResponse.data.namespaces
        const namespaceList = nsList.map((ns: string | { namespace?: string; name?: string; vector_count?: number }, index: number) => {
          if (typeof ns === 'object') {
            return {
              id: `namespace_${index}`,
              name: ns.namespace || ns.name || '',
              document_count: ns.vector_count || 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          }
          return {
            id: `namespace_${index}`,
            name: ns,
            document_count: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        })
        setNamespaces(namespaceList)
      }

      toast.success(`Successfully uploaded ${files.length} file(s) to namespace "${namespace}"`, 'Upload Complete')
      trackEvent('upload_completed', {
        file_count: files.length,
        category: category || 'general',
      })
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string; error?: string; existing_document?: { namespace?: string } } } }
      if (axiosError?.response?.status === 409) {
        const existing = axiosError.response.data?.existing_document
        toast.warning(
          `File already exists in namespace "${existing?.namespace || namespace}". Use a different file or re-upload with force.`,
          'Duplicate File'
        )
      } else {
        alert.error('Upload Failed', axiosError?.response?.data?.error || axiosError?.response?.data?.message || 'Please try again.')
      }
      trackEvent('upload_failed', {
        file_count: files.length,
        status: axiosError?.response?.status || 'unknown',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handlePageChange = (page: string) => {
    setCurrentPage(page as PageType)
    if (page !== 'landing') {
      setShowSidebar(true)
    }
  }

  const handleGetStarted = () => {
    setCurrentPage('upload')
    setShowSidebar(true)
  }

  const handleLoggedOutGetStarted = () => {
    try { sessionStorage.setItem('postAuthRedirect', 'upload') } catch {}
    trackEvent('signup_intent_created', { source: 'landing_cta', target: 'upload' })
    setCurrentPage('register')
    setShowSidebar(false)
  }

  const handleViewDashboard = () => {
    setCurrentPage('dashboard')
    setShowSidebar(true)
  }

  const handleStartAudit = () => {
    setCurrentPage('audits')
  }

  const handleGoToBilling = () => {
    setCurrentPage('billing')
  }

  const handleViewAudit = (auditId: string) => {
    setSelectedAuditId(auditId)
    setCurrentPage('audit')
  }

  const handleBackFromAudit = () => {
    setSelectedAuditId(undefined)
    setCurrentPage('audits')
  }

  const handleLogout = async () => {
    try {
      await authService.logout()
    } finally {
      dispatch(logout())
      setCurrentPage('landing')
      setShowSidebar(false)
    }
  }

  const handleLoginSuccess = () => {
    let redirectTo: PageType = 'dashboard'
    try {
      const savedRedirect = sessionStorage.getItem('postAuthRedirect')
      if (savedRedirect === 'upload') {
        redirectTo = 'upload'
        sessionStorage.removeItem('postAuthRedirect')
      }
    } catch {}
    setCurrentPage(redirectTo)
    setShowSidebar(true)
  }

  const handleGoToLogin = () => {
    setCurrentPage('login')
  }

  const handleGoToRegister = () => {
    setCurrentPage('register')
  }

  const handleGoToForgotPassword = () => {
    setCurrentPage('forgot-password')
  }

  const handleResendVerification = async () => {
    setResendingVerification(true)
    try {
      await authService.resendVerification()
      toast.success('Verification email sent! Check your inbox.', 'Email Sent')
    } catch {
      toast.error('Failed to send verification email. Please try again.', 'Error')
    } finally {
      setResendingVerification(false)
    }
  }

  const handleVerifyEmailSuccess = async () => {
    // Refresh user data to get is_verified=true BEFORE navigating
    try {
      const response = await authService.getCurrentUser()
      dispatch(updateUser(response.data.user))
    } catch {}
    setBannerDismissed(true)
    setCurrentPage('dashboard')
    setShowSidebar(true)
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'landing':
        return (
          <LandingPage
            onGetStarted={isAuthenticated ? handleGetStarted : handleLoggedOutGetStarted}
            onViewDashboard={isAuthenticated ? handleViewDashboard : handleGoToLogin}
            onContact={() => setCurrentPage('contact')}
            onCheckout={isAuthenticated ? handleGoToBilling : handleGoToLogin}
            onTerms={() => setCurrentPage('terms')}
            onPrivacy={() => setCurrentPage('privacy')}
            onRefundPolicy={() => setCurrentPage('refund-policy')}
            onCookiePolicy={() => setCurrentPage('cookie-policy')}
          />
        )
      case 'login':
        return (
          <LoginPage
            onRegister={handleGoToRegister}
            onForgotPassword={handleGoToForgotPassword}
            onSuccess={handleLoginSuccess}
          />
        )
      case 'register':
        return (
          <RegisterPage
            onLogin={handleGoToLogin}
            onSuccess={handleLoginSuccess}
          />
        )
      case 'forgot-password':
        return (
          <ForgotPasswordPage
            onBack={handleGoToLogin}
          />
        )
      case 'reset-password':
        return (
          <ResetPasswordPage
            token={resetToken}
            onSuccess={handleGoToLogin}
            onBack={handleGoToLogin}
          />
        )
      case 'verify-email':
        return (
          <VerifyEmailPage
            token={verifyToken}
            onSuccess={handleVerifyEmailSuccess}
            onBack={() => {
              setCurrentPage(isAuthenticated ? 'dashboard' : 'landing')
              if (isAuthenticated) setShowSidebar(true)
            }}
          />
        )
      case 'shared-audit':
        return <SharedAuditPage shareToken={sharedAuditToken} />
      case 'contact':
        return (
          <ContactUsPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'terms':
        return (
          <TermsPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'privacy':
        return (
          <PrivacyPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'refund-policy':
        return (
          <RefundPolicyPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'cookie-policy':
        return (
          <CookiePolicyPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'security':
        return (
          <SecurityPage
            onBack={() => setCurrentPage('landing')}
          />
        )
      case 'dashboard':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return (
          <Dashboard
            onStartAudit={handleStartAudit}
            onViewAudit={handleViewAudit}
          />
        )
      case 'audits':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return (
          <AuditsPage
            onViewAudit={handleViewAudit}
            onGoToBilling={handleGoToBilling}
          />
        )
      case 'audit':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return (
          <AuditView
            auditId={selectedAuditId}
            onBack={handleBackFromAudit}
          />
        )
      case 'upload':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return (
          <UploadPage
            namespaces={namespaces}
            onUpload={handleUpload}
            isUploading={isUploading}
          />
        )
      case 'regulations':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return <RegulationsPage />
      case 'chat':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return <ChatInterface />
      case 'billing':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return <BillingPage />
      case 'feedback':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return <FeedbackPage />
      case 'settings':
        if (!isAuthenticated) return <LoginPage onRegister={handleGoToRegister} onForgotPassword={handleGoToForgotPassword} onSuccess={handleLoginSuccess} />
        return (
          <SettingsPage
            user={user}
            onUserUpdate={(updatedUser) => dispatch(updateUser(updatedUser))}
            onNavigate={handlePageChange}
          />
        )
      default:
        return (
          <Dashboard
            onStartAudit={handleStartAudit}
            onViewAudit={handleViewAudit}
          />
        )
    }
  }

  const authPages: PageType[] = ['landing', 'login', 'register', 'forgot-password', 'reset-password', 'verify-email', 'contact', 'terms', 'privacy', 'refund-policy', 'cookie-policy', 'security', 'shared-audit']
  if (authPages.includes(currentPage)) {
    return (
      <>
        <Suspense fallback={<RouteFallback />}>
          {renderCurrentPage()}
        </Suspense>
        <CookieConsent onCookiePolicy={() => setCurrentPage('cookie-policy')} />
      </>
    )
  }

  const closeMobileSidebar = () => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }
  }

  return (
    <AnalysisNotificationProvider onViewAudit={handleViewAudit}>
      <div className="flex h-screen bg-gray-50 dark:bg-dark-bg transition-colors">
          {showSidebar && (
            <>
              {sidebarOpen && (
                <div
                  className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
                  onClick={() => setSidebarOpen(false)}
                />
              )}
              <Suspense fallback={null}>
                <Sidebar
                  isOpen={sidebarOpen}
                  onToggle={() => setSidebarOpen(!sidebarOpen)}
                  currentPage={currentPage}
                  onPageChange={(page) => {
                    handlePageChange(page)
                    closeMobileSidebar()
                  }}
                  onClose={() => setSidebarOpen(false)}
                  onLogout={handleLogout}
                  user={user}
                />
              </Suspense>
            </>
          )}

          <main className="flex-1 flex flex-col overflow-auto relative">
            {/* Mobile header */}
            {showSidebar && (
              <div className="md:hidden flex items-center justify-between p-3 border-b border-gray-100 dark:border-dark-border bg-white dark:bg-dark-surface">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-surface-2 transition-colors"
                    aria-label="Open menu"
                  >
                    <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                  </button>
                  <span className="font-semibold text-gray-900 dark:text-white">AuditGuard<span className="text-brand-500">.</span></span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-500 hover:text-red-600 transition-colors"
                  aria-label="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Desktop sidebar toggle - shows when sidebar is closed */}
            {showSidebar && !sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="hidden md:flex fixed top-4 left-4 z-30 items-center justify-center w-10 h-10 bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border rounded-xl shadow-soft hover:bg-gray-50 dark:hover:bg-dark-surface-2 transition-colors"
                aria-label="Open sidebar"
              >
                <PanelLeftOpen className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}

            {/* Email verification banner */}
            {isAuthenticated && user && user.is_verified === false && !bannerDismissed && (
              <div className="bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20 px-4 py-3">
                <div className="flex items-center justify-between gap-3 max-w-5xl mx-auto">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      Please verify your email address. Check your inbox or{' '}
                      <button
                        onClick={handleResendVerification}
                        disabled={resendingVerification}
                        className="font-semibold underline hover:no-underline disabled:opacity-50"
                      >
                        {resendingVerification ? 'Sending...' : 'resend verification email'}
                      </button>
                    </p>
                  </div>
                  <button
                    onClick={() => setBannerDismissed(true)}
                    className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors shrink-0"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </button>
                </div>
              </div>
            )}

            <Suspense fallback={<RouteFallback />}>
              {renderCurrentPage()}
            </Suspense>
          </main>
        </div>
        <CookieConsent onCookiePolicy={() => setCurrentPage('cookie-policy')} />
    </AnalysisNotificationProvider>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <NotificationProvider>
              <AppContent />
            </NotificationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}

export default App
